import React, { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Routes, Route, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import { BarChart3, Users, FileText, Briefcase, TrendingUp, Clock, AlertTriangle, CheckCircle2, GraduationCap, Building2, Search, Shield, ShieldAlert, XCircle, MapPin, Calendar, Star, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { EMPLOYMENT_TYPE_LABELS, EXPERIENCE_LEVEL_LABELS, SALARY_LABELS } from '../constants/profileOptions'
import { LineChart, Line, ResponsiveContainer } from 'recharts'
import toast from 'react-hot-toast'
import { useAdminScope } from '../hooks/useAdminScope'
import ManageAlumni from './ManageAlumni'
import ManageEmployers from './ManageEmployers'
import ManagePrograms from './ManagePrograms'
import ManageNews from './ManageNews'
import ManageSurveys from './ManageSurveys'
import AdminProfilePage from './AdminProfilePage'
import AnalyticsPage from './AnalyticsPage'
import ReportsPage from './ReportsPage'

interface DashboardStats {
  totalAlumni: number
  employmentRate: number
  unemployedAlumni: number
  unemploymentRate: number
  activeSurveys: number
  activeSurveyResponseRate: number
  activeAlumni: number
  inactiveAlumni: number
}

const AdminDashboard: React.FC = () => {
  return (
    <Routes>
      <Route index element={<AdminOverview />} />
      <Route path="profile" element={<AdminProfilePage />} />
      <Route path="analytics" element={<AnalyticsPage />} />
      <Route path="alumni" element={<ManageAlumni />} />
      <Route path="employers" element={<ManageEmployers />} />
      <Route path="surveys" element={<ManageSurveys />} />
      <Route path="jobs" element={<JobManagementPage />} />
      <Route path="news" element={<ManageNews />} />
      <Route path="programs" element={<ManagePrograms />} />
      <Route path="reports" element={<ReportsPage />} />
    </Routes>
  )
}

const AdminOverview: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { isSuperAdmin, allowedDepartments, department, loading: scopeLoading } = useAdminScope()
  const [stats, setStats] = useState<DashboardStats>({
    totalAlumni: 0,
    employmentRate: 0,
    unemployedAlumni: 0,
    unemploymentRate: 0,
    activeSurveys: 0,
    activeSurveyResponseRate: 0,
    activeAlumni: 0,
    inactiveAlumni: 0,
  })
  const [loading, setLoading] = useState(true)
  const [registrationTrendData, setRegistrationTrendData] = useState<any[]>([])
  const [recentSurveys, setRecentSurveys] = useState<any[]>([])
  const [unemployedAlumniList, setUnemployedAlumniList] = useState<any[]>([])
  const [pendingApprovals, setPendingApprovals] = useState(0)
  const [pendingEmployers, setPendingEmployers] = useState(0)
  const [surveysDueSoon, setSurveysDueSoon] = useState<{ title: string; deadline: string }[]>([])
  const [programStats, setProgramStats] = useState<{ program: string; total: number; laborForce: number; employed: number; rate: number }[]>([])
  const [newAlumniThisMonth, setNewAlumniThisMonth] = useState(0)
  const [surveyResponsesThisWeek, setSurveyResponsesThisWeek] = useState(0)

  useEffect(() => {
    if (!scopeLoading) fetchDashboardData()
  }, [isSuperAdmin, allowedDepartments, scopeLoading])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)

      const deptFilter = !isSuperAdmin && allowedDepartments && allowedDepartments.length > 0

      // Fetch total alumni count
      let totalAlumniQ = supabaseClient
        .from('alumni_profiles')
        .select('*', { count: 'exact', head: true })
        .eq('approval_status', 'approved')
      if (deptFilter) totalAlumniQ = totalAlumniQ.in('department', allowedDepartments!)
      const { count: totalAlumni } = await totalAlumniQ

      // Fetch employment statistics
      let profilesQ = supabaseClient
        .from('alumni_profiles')
        .select('current_employment_status, updated_at, user_id, program, first_name, last_name, year_graduated, created_at')
        .eq('approval_status', 'approved')
      if (deptFilter) profilesQ = profilesQ.in('department', allowedDepartments!)
      const { data: alumniProfiles } = await profilesQ

      // Debug: Log all employment statuses with actual values
      console.log('=== EMPLOYMENT DEBUG ===')
      console.log('Total Alumni (from users table):', totalAlumni)
      console.log('Total Alumni Profiles:', alumniProfiles?.length)
      console.table(alumniProfiles?.map(p => ({
        user_id: p.user_id?.substring(0, 8) + '...',
        status: p.current_employment_status || 'NO STATUS SET'
      })))

      // Handle both hyphenated and underscored versions of enum values
      const employed = alumniProfiles?.filter(
        (p) => {
          const status = p.current_employment_status?.toLowerCase()
          return status === 'employed' || status === 'self-employed' || status === 'self_employed' || status === 'studying_employed'
        }
      ).length || 0
      
      const unemployed = alumniProfiles?.filter(
        (p) => p.current_employment_status?.toLowerCase() === 'unemployed'
      ).length || 0
      
      const totalWithStatus = alumniProfiles?.filter((p) => p.current_employment_status).length || 0
      // Labor force: exclude alumni who are purely studying (not in the workforce)
      const laborForce = alumniProfiles
        ? alumniProfiles.filter((p) => p.current_employment_status?.toLowerCase() !== 'studying').length
        : (totalAlumni || 0)
      
      console.log('Employed count:', employed)
      console.log('Unemployed count:', unemployed)
      console.log('Profiles with status set:', totalWithStatus)
      console.log('Labor force (excl. studying):', laborForce)
      console.log('======================')            

      // Compute per-program employment stats
      const programMap: Record<string, { total: number; laborForce: number; employed: number }> = {}
      alumniProfiles?.forEach((p: any) => {
        const prog = p.program || 'Unknown'
        if (!programMap[prog]) programMap[prog] = { total: 0, laborForce: 0, employed: 0 }
        programMap[prog].total++
        const s = p.current_employment_status?.toLowerCase()
        if (s !== 'studying') programMap[prog].laborForce++
        if (s === 'employed' || s === 'self-employed' || s === 'self_employed' || s === 'studying_employed') programMap[prog].employed++
      })
      setProgramStats(
        Object.entries(programMap)
          .map(([program, { total, laborForce: lf, employed: emp }]) => ({
            program,
            total,
            laborForce: lf,
            employed: emp,
            rate: lf > 0 ? Math.round((emp / lf) * 100) : 0,
          }))
          .sort((a, b) => b.total - a.total)
          .slice(0, 8)
      )

      // New alumni registered this month (scoped by dept already via alumniProfiles)
      const firstOfMonth = new Date()
      firstOfMonth.setDate(1)
      firstOfMonth.setHours(0, 0, 0, 0)
      setNewAlumniThisMonth(
        alumniProfiles?.filter((p: any) => p.created_at && new Date(p.created_at) >= firstOfMonth).length || 0
      )

      // Survey responses received this week — scoped by dept alumni user_ids if dept admin
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
      let weekResponsesQ = supabaseClient
        .from('survey_responses')
        .select('*', { count: 'exact', head: true })
        .gte('submitted_at', sevenDaysAgo)
      if (deptFilter && alumniProfiles && alumniProfiles.length > 0) {
        const alumniUserIds = alumniProfiles.map((p: any) => p.user_id).filter(Boolean)
        if (alumniUserIds.length > 0) weekResponsesQ = weekResponsesQ.in('user_id', alumniUserIds)
        else weekResponsesQ = weekResponsesQ.in('user_id', ['00000000-0000-0000-0000-000000000000'])
      }
      const { count: weekResponseCount } = await weekResponsesQ
      setSurveyResponsesThisWeek(weekResponseCount || 0)

      // Calculate rates based on labor force (excludes purely-studying alumni)
      const employmentRate = laborForce > 0 ? (employed / laborForce) * 100 : 0
      const unemploymentRate = laborForce > 0 ? (unemployed / laborForce) * 100 : 0

      // Fetch active surveys (with department scoping for dept admins)
      let activeSurveysQuery = supabaseClient
        .from('surveys')
        .select('id, created_by, target_departments')
        .eq('status', 'active')

      const { data: activeSurveysData } = await activeSurveysQuery

      // Filter surveys for department admins
      let visibleActiveSurveys = activeSurveysData || []
      if (!isSuperAdmin && activeSurveysData) {
        visibleActiveSurveys = activeSurveysData.filter((survey: any) => {
          // Show if created by this admin
          if (survey.created_by === user?.id) return true
          
          // Show if no targeting (targets all alumni)
          if (!survey.target_departments || survey.target_departments.length === 0) return true
          
          // Show if survey targets admin's department(s)
          if (allowedDepartments && survey.target_departments.some((d: string) => allowedDepartments.includes(d))) {
            return true
          }
          
          return false
        })
      }

      const activeSurveys = visibleActiveSurveys.length

      // Calculate active survey response rate
      let activeSurveyResponseRate = 0
      if (visibleActiveSurveys.length > 0 && totalAlumni && totalAlumni > 0) {
        // Get all responses to active surveys
        const { data: activeResponses } = await supabaseClient
          .from('survey_responses')
          .select('user_id')
          .in('survey_id', visibleActiveSurveys.map((s: any) => s.id))
        
        // Count unique alumni who have responded to at least one active survey
        const uniqueRespondents = new Set(activeResponses?.map(r => r.user_id) || [])
        activeSurveyResponseRate = (uniqueRespondents.size / totalAlumni) * 100
      }

      // Calculate active/inactive alumni (based on last 30 days of activity)
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
      const activeAlumni =
        alumniProfiles?.filter((p) => new Date(p.updated_at) > thirtyDaysAgo).length || 0
      const inactiveAlumni = (totalAlumni || 0) - activeAlumni

      setStats({
        totalAlumni: totalAlumni || 0,
        employmentRate: Math.round(employmentRate * 10) / 10,
        unemployedAlumni: unemployed,
        unemploymentRate: Math.round(unemploymentRate * 10) / 10,
        activeSurveys: activeSurveys || 0,
        activeSurveyResponseRate: Math.round(activeSurveyResponseRate * 10) / 10,
        activeAlumni,
        inactiveAlumni,
      })

      // Fetch registration trend (last 4 months)
      let trendQ = supabaseClient
        .from('users')
        .select('created_at')
        .eq('role', 'alumni')
        .gte('created_at', new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: true })
      if (deptFilter) {
        const profileUserIds = (alumniProfiles || []).map((p: any) => p.user_id).filter(Boolean)
        if (profileUserIds.length > 0) trendQ = trendQ.in('id', profileUserIds)
        else trendQ = trendQ.in('id', ['00000000-0000-0000-0000-000000000000']) // no results
      }
      const { data: users } = await trendQ

      const monthlyData = users?.reduce((acc: any, user: any) => {
        const month = new Date(user.created_at).toLocaleString('default', { month: 'short' })
        const existing = acc.find((m: any) => m.month === month)
        if (existing) {
          existing.registrations++
        } else {
          acc.push({ month, registrations: 1 })
        }
        return acc
      }, [])

      setRegistrationTrendData(monthlyData?.slice(-4) || [])

      // Fetch recent surveys with response counts (with department scoping for dept admins)
      const { data: surveysData } = await supabaseClient
        .from('surveys')
        .select('id, title, created_at, created_by, target_departments')
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(10) // Fetch more since we'll filter

      // Filter surveys for department admins
      let visibleSurveys = surveysData || []
      if (!isSuperAdmin && surveysData) {
        visibleSurveys = surveysData.filter((survey: any) => {
          // Show if created by this admin
          if (survey.created_by === user?.id) return true
          
          // Show if no targeting (targets all alumni)
          if (!survey.target_departments || survey.target_departments.length === 0) return true
          
          // Show if survey targets admin's department(s)
          if (allowedDepartments && survey.target_departments.some((d: string) => allowedDepartments.includes(d))) {
            return true
          }
          
          return false
        })
      }

      // Take only top 3 after filtering
      const surveys = visibleSurveys.slice(0, 3)

      const surveysWithCounts = await Promise.all(
        surveys.map(async (survey: any) => {
          const { count } = await supabaseClient
            .from('survey_responses')
            .select('*', { count: 'exact', head: true })
            .eq('survey_id', survey.id)
            .gte('submitted_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())

          return {
            title: survey.title,
            newResponses: count || 0,
          }
        })
      )

      setRecentSurveys(surveysWithCounts)

      // Fetch unemployed alumni details - use lowercase enum value only
      let unemployedQ = supabaseClient
        .from('alumni_profiles')
        .select('user_id, first_name, last_name, year_graduated, current_employment_status, unemployment_reasons')
        .eq('approval_status', 'approved')
        .eq('current_employment_status', 'unemployed')
        .order('updated_at', { ascending: false })
        .limit(8)
      if (deptFilter) unemployedQ = unemployedQ.in('department', allowedDepartments!)
      const { data: unemployedDetails, error: unemployedError } = await unemployedQ

      if (unemployedError) {
        console.error('Error fetching unemployed:', unemployedError)
      }

      console.log('Unemployed Details:', unemployedDetails)
      console.log('Expected count:', unemployed, 'Fetched count:', unemployedDetails?.length)

      const formattedUnemployed = unemployedDetails?.map((alumni) => ({
        id: alumni.user_id,
        name: `${alumni.first_name || 'N/A'} ${alumni.last_name || ''}`.trim() || 'Unknown',
        batch: alumni.year_graduated?.toString() || 'N/A',
        unemploymentReason: alumni.unemployment_reasons || '—',
        status: 'Seeking',
      }))

      setUnemployedAlumniList(formattedUnemployed || [])

      // Fetch pending alumni approvals
      let pendingApprovalsQ = supabaseClient
        .from('alumni_profiles')
        .select('*', { count: 'exact', head: true })
        .eq('approval_status', 'pending')
      if (deptFilter) pendingApprovalsQ = pendingApprovalsQ.in('department', allowedDepartments!)
      const { count: pendingCount } = await pendingApprovalsQ
      setPendingApprovals(pendingCount || 0)

      // Fetch pending employer partnership requests
      try {
        if (isSuperAdmin) {
          const { data: pendingPartners } = await supabaseClient.rpc('get_all_pending_partnerships')
          setPendingEmployers((pendingPartners || []).length)
        } else if (allowedDepartments && allowedDepartments.length > 0) {
          let employerCount = 0
          for (const dept of allowedDepartments) {
            const { data: deptPartners } = await supabaseClient.rpc('get_pending_partnerships_for_department', { dept_name: dept })
            employerCount += (deptPartners || []).length
          }
          setPendingEmployers(employerCount)
        }
      } catch {
        // RPCs may not exist yet — silently ignore
      }

      // Fetch surveys closing within 7 days
      const sevenDaysFromNow = new Date()
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7)
      const { data: dueSoonData } = await supabaseClient
        .from('surveys')
        .select('id, title, deadline, created_by, target_departments')
        .eq('status', 'active')
        .not('deadline', 'is', null)
        .lte('deadline', sevenDaysFromNow.toISOString())
        .gte('deadline', new Date().toISOString())
        .order('deadline', { ascending: true })
      let visibleDueSoon = dueSoonData || []
      if (!isSuperAdmin && dueSoonData) {
        visibleDueSoon = dueSoonData.filter((survey: any) => {
          if (survey.created_by === user?.id) return true
          if (!survey.target_departments || survey.target_departments.length === 0) return true
          if (allowedDepartments && survey.target_departments.some((d: string) => allowedDepartments.includes(d))) return true
          return false
        })
      }
      setSurveysDueSoon(visibleDueSoon.slice(0, 3).map((s: any) => ({ title: s.title, deadline: s.deadline })))
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      {/* Welcome Banner */}
      <div className="mb-8 bg-blue-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
        <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />
        <div className="relative">
          <div className="flex flex-wrap items-center gap-3 mb-1">
            <h1 className="text-2xl sm:text-3xl font-bold">
              Welcome back, {user?.email?.split('@')[0] || 'Admin'}!
            </h1>
            {!scopeLoading && (
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                isSuperAdmin ? 'bg-purple-400/30 text-purple-100' : 'bg-white/20 text-white'
              }`}>
                {isSuperAdmin ? 'Super Admin' : department || 'Department Admin'}
              </span>
            )}
          </div>
          <p className="text-blue-200 text-xs sm:text-sm">
            {isSuperAdmin ? 'University-wide management dashboard' : `${department || 'Department'} management dashboard`}
          </p>
          {!loading && (
            <div className="mt-5 flex flex-wrap gap-3">
              <span className="bg-white/10 rounded-full px-3 py-1.5 text-sm font-medium text-white flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-yellow-300" />
                {newAlumniThisMonth} new alumni this month
              </span>
              <span className="bg-yellow-400 text-gray-900 rounded-full px-3 py-1.5 text-sm font-bold flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" />
                {surveyResponsesThisWeek} survey responses this week
              </span>
              <span className={`rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 ${
                stats.unemploymentRate > 20 ? 'bg-red-400/30 text-red-100' :
                stats.unemploymentRate > 10 ? 'bg-orange-400/30 text-orange-100' :
                'bg-white/10 text-white'
              }`}>
                <Briefcase className="w-3.5 h-3.5" />
                {stats.unemploymentRate.toFixed(1)}% unemployment rate
              </span>
            </div>
          )}
        </div>
      </div>

      {/* KPI Cards (Option D) */}
      <div className="mb-12">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-6">Overview</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
          {/* Total Alumni */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="bg-blue-900 px-5 py-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-white/80" />
              <span className="text-sm font-medium text-white/90">Total Alumni</span>
            </div>
            <div className="px-5 py-4">
              <p className="text-3xl font-bold text-gray-900">{loading ? '—' : stats.totalAlumni.toLocaleString()}</p>
              {!loading && (
                <p className={`text-xs mt-1.5 flex items-center gap-1 font-medium ${newAlumniThisMonth > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                  <TrendingUp className="w-3 h-3" />
                  {newAlumniThisMonth > 0 ? `+${newAlumniThisMonth} joined this month` : 'No new joiners this month'}
                </p>
              )}
              <p className="text-xs text-gray-400 mt-1">Registered &amp; approved</p>
            </div>
          </div>

          {/* Employment Rate */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="bg-yellow-400 px-5 py-3 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-gray-800" />
              <span className="text-sm font-medium text-gray-800">Employment Rate</span>
            </div>
            <div className="px-5 py-4">
              <p className="text-3xl font-bold text-gray-900">{loading ? '—' : `${stats.employmentRate.toFixed(1)}%`}</p>
              <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-yellow-400 rounded-full transition-all duration-700" style={{ width: loading ? '0%' : `${Math.min(stats.employmentRate, 100)}%` }} />
              </div>
              <p className="text-xs text-gray-400 mt-1.5">{loading ? '' : `${stats.unemployedAlumni} currently unemployed`}</p>
            </div>
          </div>

          {/* Active Surveys */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="bg-blue-900 px-5 py-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-white/80" />
              <span className="text-sm font-medium text-white/90">Active Surveys</span>
            </div>
            <div className="px-5 py-4">
              <p className="text-3xl font-bold text-gray-900">{loading ? '—' : stats.activeSurveys.toLocaleString()}</p>
              <p className="text-xs text-blue-600 mt-1.5 font-medium">{loading ? '' : `${stats.activeSurveyResponseRate.toFixed(0)}% response rate`}</p>
              <p className="text-xs text-gray-400 mt-1">Currently deployed</p>
            </div>
          </div>

          {/* Alumni Activity */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="bg-yellow-400 px-5 py-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-gray-800" />
              <span className="text-sm font-medium text-gray-800">Alumni Activity</span>
            </div>
            <div className="px-5 py-4">
              <p className="text-3xl font-bold text-gray-900">{loading ? '—' : stats.activeAlumni.toLocaleString()}</p>
              <p className="text-xs text-red-500 mt-1.5 font-medium">{loading ? '' : `${stats.inactiveAlumni} not updated (30+ days)`}</p>
              <p className="text-xs text-gray-400 mt-1">Profile updated in last 30 days</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Actions — horizontal drag carousel */}
      <div className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Attention Needed</h2>
          {!loading && (pendingApprovals > 0 || surveysDueSoon.length > 0 || pendingEmployers > 0) && (
            <span className="px-2.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
              {pendingApprovals + surveysDueSoon.length + pendingEmployers} item{pendingApprovals + surveysDueSoon.length + pendingEmployers !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <AttentionCarousel>

          {/* Pending Alumni Approvals */}
          <button onClick={() => navigate('/admin/alumni', { state: { tab: 'pending' } })} className="text-left group flex-shrink-0 w-72">
            <div className={`rounded-xl p-5 shadow-sm hover:shadow-md transition-all border-l-4 h-full ${
              !loading && pendingApprovals > 0 ? 'bg-yellow-50 border-yellow-400' : 'bg-white border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  !loading && pendingApprovals > 0 ? 'bg-yellow-400' : 'bg-gray-100'
                }`}>
                  <Users className={`w-5 h-5 ${!loading && pendingApprovals > 0 ? 'text-gray-800' : 'text-gray-400'}`} />
                </div>
                {!loading && pendingApprovals > 0 && (
                  <span className="px-2.5 py-1 bg-yellow-400 text-gray-800 rounded-full text-sm font-bold leading-none">
                    {pendingApprovals}
                  </span>
                )}
                {!loading && pendingApprovals === 0 && (
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                )}
              </div>
              <p className="font-semibold text-gray-900 text-sm">Pending Alumni Approvals</p>
              <p className="text-xs text-gray-500 mt-1">
                {loading ? 'Loading...' : pendingApprovals > 0
                  ? `${pendingApprovals} alumni awaiting review`
                  : 'All approvals up to date'}
              </p>
              <p className="text-xs text-blue-700 mt-3 font-medium group-hover:underline">Manage Alumni →</p>
            </div>
          </button>

          {/* Surveys Closing Soon */}
          <button onClick={() => navigate('/admin/surveys')} className="text-left group flex-shrink-0 w-72">
            <div className={`rounded-xl p-5 shadow-sm hover:shadow-md transition-all border-l-4 h-full ${
              !loading && surveysDueSoon.length > 0 ? 'bg-orange-50 border-orange-400' : 'bg-white border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  !loading && surveysDueSoon.length > 0 ? 'bg-orange-400' : 'bg-gray-100'
                }`}>
                  <Clock className={`w-5 h-5 ${!loading && surveysDueSoon.length > 0 ? 'text-white' : 'text-gray-400'}`} />
                </div>
                {!loading && surveysDueSoon.length > 0 && (
                  <span className="px-2.5 py-1 bg-orange-400 text-white rounded-full text-sm font-bold leading-none">
                    {surveysDueSoon.length}
                  </span>
                )}
                {!loading && surveysDueSoon.length === 0 && (
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                )}
              </div>
              <p className="font-semibold text-gray-900 text-sm">Surveys Closing Soon</p>
              <p className="text-xs text-gray-500 mt-1">
                {loading ? 'Loading...' : surveysDueSoon.length > 0
                  ? `${surveysDueSoon.length} survey${surveysDueSoon.length !== 1 ? 's' : ''} due within 7 days`
                  : 'No surveys closing soon'}
              </p>
              {!loading && surveysDueSoon.length > 0 && (
                <p className="text-xs text-orange-700 mt-1 truncate">Next: {surveysDueSoon[0].title}</p>
              )}
              <p className="text-xs text-blue-700 mt-3 font-medium group-hover:underline">Manage Surveys →</p>
            </div>
          </button>

          {/* Pending Employer Partnerships */}
          <button onClick={() => navigate('/admin/employers', { state: { tab: 'pending' } })} className="text-left group flex-shrink-0 w-72">
            <div className={`rounded-xl p-5 shadow-sm hover:shadow-md transition-all border-l-4 h-full ${
              !loading && pendingEmployers > 0 ? 'bg-purple-50 border-purple-500' : 'bg-white border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  !loading && pendingEmployers > 0 ? 'bg-purple-500' : 'bg-gray-100'
                }`}>
                  <Building2 className={`w-5 h-5 ${
                    !loading && pendingEmployers > 0 ? 'text-white' : 'text-gray-400'
                  }`} />
                </div>
                {!loading && pendingEmployers > 0 && (
                  <span className="px-2.5 py-1 bg-purple-500 text-white rounded-full text-sm font-bold leading-none">
                    {pendingEmployers}
                  </span>
                )}
                {!loading && pendingEmployers === 0 && (
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                )}
              </div>
              <p className="font-semibold text-gray-900 text-sm">Pending Employer Partnerships</p>
              <p className="text-xs text-gray-500 mt-1">
                {loading ? 'Loading...' : pendingEmployers > 0
                  ? `${pendingEmployers} partnership request${pendingEmployers !== 1 ? 's' : ''} awaiting review`
                  : 'No pending partnership requests'}
              </p>
              <p className="text-xs text-blue-700 mt-3 font-medium group-hover:underline">Manage Employers →</p>
            </div>
          </button>

          {/* Survey Response Rate Alert */}
          <button onClick={() => navigate('/admin/surveys')} className="text-left group flex-shrink-0 w-72">
            <div className={`rounded-xl p-5 shadow-sm hover:shadow-md transition-all border-l-4 h-full ${
              !loading && stats.activeSurveys > 0 && stats.activeSurveyResponseRate < 50
                ? 'bg-blue-50 border-blue-900'
                : 'bg-white border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  !loading && stats.activeSurveys > 0 && stats.activeSurveyResponseRate < 50
                    ? 'bg-blue-900'
                    : 'bg-gray-100'
                }`}>
                  <AlertTriangle className={`w-5 h-5 ${
                    !loading && stats.activeSurveys > 0 && stats.activeSurveyResponseRate < 50
                      ? 'text-yellow-400'
                      : 'text-gray-400'
                  }`} />
                </div>
                {!loading && stats.activeSurveys > 0 && (
                  <span className={`px-2.5 py-1 rounded-full text-sm font-bold leading-none ${
                    stats.activeSurveyResponseRate < 50
                      ? 'bg-blue-900 text-white'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {stats.activeSurveyResponseRate.toFixed(0)}%
                  </span>
                )}
                {!loading && stats.activeSurveys === 0 && (
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                )}
              </div>
              <p className="font-semibold text-gray-900 text-sm">Survey Response Rate</p>
              <p className="text-xs text-gray-500 mt-1">
                {loading ? 'Loading...' : stats.activeSurveys === 0
                  ? 'No active surveys deployed'
                  : stats.activeSurveyResponseRate < 50
                    ? `${Math.round(stats.totalAlumni * (1 - stats.activeSurveyResponseRate / 100))} alumni yet to respond`
                    : `${stats.activeSurveyResponseRate.toFixed(0)}% response rate — on track`}
              </p>
              <p className="text-xs text-blue-700 mt-3 font-medium group-hover:underline">View Surveys →</p>
            </div>
          </button>

        </AttentionCarousel>
      </div>

      {/* Program Insights Section */}
      {programStats.length > 0 && (
        <div className="mb-12">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-6">Employability by Program</h2>
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="bg-blue-900 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-white">Employment Rate per Program</h3>
                <p className="text-xs text-blue-200 mt-0.5">Based on approved alumni with employment status recorded</p>
              </div>
              <GraduationCap className="w-5 h-5 text-blue-300" />
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
              {loading ? (
                <p className="text-sm text-gray-500">Loading...</p>
              ) : (
                programStats.map((prog, idx) => (
                  <div key={prog.program}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium text-gray-700 truncate max-w-[60%]">{prog.program}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">{prog.employed}/{prog.laborForce}</span>
                        <span className={`text-sm font-bold w-10 text-right ${
                          prog.rate >= 70 ? 'text-green-600' : prog.rate >= 40 ? 'text-yellow-600' : 'text-red-500'
                        }`}>{prog.rate}%</span>
                      </div>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${idx % 2 === 0 ? 'bg-blue-900' : 'bg-yellow-400'}`}
                        style={{ width: `${prog.rate}%` }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Key Statistics Section */}
      <div className="mb-12">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-6">Key Statistics</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Alumni Registration Trend */}
          <div className="bg-white rounded-xl p-6 shadow-sm border-t-4 border-blue-900">
            <h3 className="text-base sm:text-lg font-bold text-blue-900 mb-4">Alumni Registration Trend</h3>
            {loading ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : (
              <>
                <div className="space-y-3 mb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                      <span className="text-sm text-gray-600">New Registrations (Last 4 Months)</span>
                    </div>
                  </div>
                  {registrationTrendData.length >= 2 && (
                    <div className="pl-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                          {registrationTrendData[registrationTrendData.length - 1]?.month}:
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                          {registrationTrendData[registrationTrendData.length - 1]?.registrations || 0} alumni
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                          {registrationTrendData[registrationTrendData.length - 2]?.month}:
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                          {registrationTrendData[registrationTrendData.length - 2]?.registrations || 0} alumni
                        </span>
                      </div>
                      {registrationTrendData.length >= 2 &&
                        registrationTrendData[registrationTrendData.length - 2]?.registrations > 0 && (
                          <div className="flex items-center justify-between">
                            <span
                              className={`text-sm font-semibold ${
                                ((registrationTrendData[registrationTrendData.length - 1]?.registrations || 0) -
                                  (registrationTrendData[registrationTrendData.length - 2]?.registrations || 0)) /
                                  (registrationTrendData[registrationTrendData.length - 2]?.registrations || 1) >
                                0
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }`}
                            >
                              {(
                                (((registrationTrendData[registrationTrendData.length - 1]?.registrations || 0) -
                                  (registrationTrendData[registrationTrendData.length - 2]?.registrations || 0)) /
                                  (registrationTrendData[registrationTrendData.length - 2]?.registrations || 1)) *
                                100
                              ).toFixed(0)}
                              % growth
                            </span>
                          </div>
                        )}
                    </div>
                  )}
                </div>
                {registrationTrendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={120}>
                    <LineChart data={registrationTrendData}>
                      <Line
                        type="monotone"
                        dataKey="registrations"
                        stroke="#2563eb"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-8">No registration data available</p>
                )}
              </>
            )}
          </div>

          {/* Recent Survey Responses */}
          <div className="bg-white rounded-xl p-6 shadow-sm border-t-4 border-yellow-400">
            <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-4">Recent Survey Responses <span className="text-sm font-normal text-gray-400">(Last 7 Days)</span></h3>
            {loading ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : recentSurveys.length > 0 ? (
              <div className="space-y-4">
                {recentSurveys.map((survey, idx) => (
                  <div
                    key={idx}
                    className={`flex items-start justify-between ${idx < recentSurveys.length - 1 ? 'pb-3 border-b border-gray-100' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-gray-700">{survey.title}</span>
                    </div>
                    <span className="text-sm font-semibold text-blue-600">{survey.newResponses} new</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No recent survey responses</p>
            )}
          </div>

          {/* System Usage */}
          <div className="bg-white rounded-xl p-6 shadow-sm border-t-4 border-blue-900">
            <h3 className="text-base sm:text-lg font-bold text-blue-900 mb-4">System Usage</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-gray-600">Profile Updated (30 days)</span>
                </div>
                <span className="text-lg font-bold text-gray-900">
                  {loading ? '...' : stats.activeAlumni.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-orange-600" />
                  <span className="text-sm text-gray-600">Not Updated (30+ days)</span>
                </div>
                <span className="text-lg font-bold text-gray-900">
                  {loading ? '...' : stats.inactiveAlumni.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-gray-600">Active Survey Response Rate</span>
                </div>
                <span className="text-lg font-bold text-gray-900">
                  {loading ? '...' : `${stats.activeSurveyResponseRate.toFixed(0)}%`}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-purple-600" />
                  <span className="text-sm text-gray-600">Employment Rate</span>
                </div>
                <span className="text-lg font-bold text-gray-900">
                  {loading ? '...' : `${stats.employmentRate.toFixed(0)}%`}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Unemployed Alumni Section */}
      <div className="mb-8">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-6">
          Unemployed Alumni <span className="text-sm font-normal text-gray-500">(Priority List)</span>
        </h2>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
          <div className="bg-yellow-400 px-6 py-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-800" />
            <span className="text-sm font-semibold text-gray-800">Alumni currently seeking employment</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Batch
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unemployment Reason
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                      Loading unemployed alumni data...
                    </td>
                  </tr>
                ) : unemployedAlumniList.length > 0 ? (
                  unemployedAlumniList.map((alumni) => (
                    <tr key={alumni.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {alumni.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {alumni.batch}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {alumni.unemploymentReason}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          {alumni.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                      No unemployed alumni found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

// Horizontal drag-to-scroll carousel for Attention Needed cards
const AttentionCarousel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const startX = useRef(0)
  const scrollLeft = useRef(0)

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    isDragging.current = true
    startX.current = e.pageX - (ref.current?.offsetLeft ?? 0)
    scrollLeft.current = ref.current?.scrollLeft ?? 0
    if (ref.current) ref.current.style.cursor = 'grabbing'
  }, [])

  const onMouseLeaveOrUp = useCallback(() => {
    isDragging.current = false
    if (ref.current) ref.current.style.cursor = 'grab'
  }, [])

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current || !ref.current) return
    e.preventDefault()
    const x = e.pageX - ref.current.offsetLeft
    const walk = (x - startX.current) * 1.2
    ref.current.scrollLeft = scrollLeft.current - walk
  }, [])

  return (
    <div
      ref={ref}
      className="flex gap-4 overflow-x-auto pb-3 select-none"
      style={{ cursor: 'grab', scrollbarWidth: 'none' }}
      onMouseDown={onMouseDown}
      onMouseLeave={onMouseLeaveOrUp}
      onMouseUp={onMouseLeaveOrUp}
      onMouseMove={onMouseMove}
    >
      {children}
    </div>
  )
}

interface AdminJob {
  id: string
  title: string
  description?: string
  company_name: string
  company_id: string
  company_logo?: string | null
  status: 'active' | 'closed' | 'draft' // draft kept in type for DB compat
  employment_type: string
  experience_level?: string
  location?: string
  salary_range?: string
  skills_required?: string[]
  requirements?: string[]
  target_departments?: string[]
  is_partner_listing: boolean
  posted_at: string
  deadline?: string
  application_count: number
  posted_by_name?: string
  closed_by_name?: string
  close_reason?: string
  is_admin_closed?: boolean
  avg_rating?: number | null
  rating_count?: number | null
}

function fmtJobDate(d: string) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const jobStatusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  draft:  'bg-gray-100 text-gray-600',
  closed: 'bg-red-100 text-red-700',
}

const JobManagementPage: React.FC = () => {
  const { isSuperAdmin, loading: scopeLoading } = useAdminScope()
  const [jobs, setJobs] = useState<AdminJob[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'closed'>('active')
  const [search, setSearch] = useState('')
  const [viewingJob, setViewingJob] = useState<AdminJob | null>(null)
  const [closeTarget, setCloseTarget] = useState<AdminJob | null>(null)
  const [closeReason, setCloseReason] = useState('')
  const [closing, setClosing] = useState(false)
  const [jobPage, setJobPage] = useState(1)

  const fetchJobs = useCallback(async () => {
    setLoading(true)
    try {
      const [{ data, error }, { data: ratingsData }] = await Promise.all([
        supabaseClient.rpc('get_all_jobs_admin'),
        supabaseClient.rpc('get_company_public_ratings'),
      ])
      if (error) throw error
      const ratingsMap = new Map<string, { avg_rating: number; rating_count: number }>()
      for (const r of (ratingsData || []) as Array<{ company_id: string; avg_rating: number; rating_count: number }>) {
        ratingsMap.set(r.company_id, r)
      }
      setJobs(((data as AdminJob[]) || []).map(j => ({
        ...j,
        avg_rating: ratingsMap.get(j.company_id)?.avg_rating ?? null,
        rating_count: ratingsMap.get(j.company_id)?.rating_count ?? null,
      })))
    } catch (err: any) {
      toast.error(err.message || 'Failed to load jobs')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (scopeLoading) return
    if (isSuperAdmin) fetchJobs()
    else setLoading(false)
  }, [scopeLoading, isSuperAdmin, fetchJobs])

  useEffect(() => { setJobPage(1) }, [statusFilter, search])

  const handleAdminClose = async () => {
    if (!closeTarget || closeReason.trim().length < 10) return
    setClosing(true)
    try {
      const { error } = await supabaseClient.rpc('admin_close_job', {
        p_job_id: closeTarget.id,
        p_reason: closeReason.trim(),
      })
      if (error) throw error
      toast.success('Job closed. Employer and affected alumni have been notified.')
      setCloseTarget(null)
      setCloseReason('')
      fetchJobs()
    } catch (err: any) {
      toast.error(err.message || 'Failed to close job listing')
    } finally {
      setClosing(false)
    }
  }

  const filtered = jobs.filter(j => {
    if (statusFilter !== 'all' && j.status !== statusFilter) return false
    if (search) {
      const q = search.toLowerCase()
      return j.title.toLowerCase().includes(q) || j.company_name.toLowerCase().includes(q)
    }
    return true
  })

  const JOB_PAGE_SIZE = 9
  const jobTotalPages = Math.max(1, Math.ceil(filtered.length / JOB_PAGE_SIZE))
  const paginatedAdminJobs = filtered.slice((jobPage - 1) * JOB_PAGE_SIZE, jobPage * JOB_PAGE_SIZE)

  const counts = {
    total:  jobs.length,
    active: jobs.filter(j => j.status === 'active').length,
    closed: jobs.filter(j => j.status === 'closed').length,
  }

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className="bg-blue-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2.5 mb-1">
            <Briefcase className="w-6 h-6 text-yellow-300" />
            <h1 className="text-2xl sm:text-3xl font-bold">Manage Job Postings</h1>
          </div>
          <p className="text-blue-200 text-sm">Review and moderate employer job listings across all companies</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <span className="bg-white/10 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
              <Briefcase className="w-3.5 h-3.5 text-yellow-300" />
              {counts.total} total
            </span>
            <span className="bg-green-500/20 border border-green-400/30 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-300" />
              {counts.active} active
            </span>

            <span className="bg-red-500/20 border border-red-400/30 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
              <XCircle className="w-3.5 h-3.5 text-red-300" />
              {counts.closed} closed
            </span>
          </div>
        </div>
      </div>

      {/* Access gate */}
      {!scopeLoading && !isSuperAdmin ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700">Access Restricted</h3>
          <p className="text-gray-500 mt-1 text-sm">Only Super Administrators can manage job postings.</p>
        </div>
      ) : (
        <>
          {/* Filter + Search */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <div className="flex gap-1.5 flex-wrap">
                {(['all', 'active', 'closed'] as const).map(s => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`px-3.5 py-2 rounded-lg text-sm font-medium transition ${
                      statusFilter === s
                        ? 'bg-blue-900 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {s === 'all'    ? `All (${counts.total})`   :
                     s === 'active' ? `Active (${counts.active})` :
                     `Closed (${counts.closed})`}
                  </button>
                ))}
              </div>
              <div className="relative sm:ml-auto sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search title or company..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Job cards */}
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-gray-300" />
              </div>
              <h3 className="text-lg font-bold text-gray-700 mb-2">
                {jobs.length === 0 ? 'No job listings yet' : 'No matching jobs'}
              </h3>
              <p className="text-sm text-gray-400">
                {jobs.length === 0 ? 'Job postings from employers will appear here.' : 'Try adjusting your filters.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {paginatedAdminJobs.map(job => {
                const iconColors = [
                  'bg-blue-900 text-white', 'bg-yellow-400 text-gray-900', 'bg-indigo-600 text-white',
                  'bg-emerald-600 text-white', 'bg-rose-600 text-white', 'bg-violet-600 text-white',
                ]
                const colorIdx = job.title.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % iconColors.length
                return (
                  <div
                    key={job.id}
                    onClick={() => setViewingJob(job)}
                    className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-200 flex flex-col group cursor-pointer"
                  >
                    <div className="p-5 flex-1 flex flex-col">
                      {/* Header row */}
                      <div className="flex items-start gap-3.5">
                        {job.company_logo ? (
                          <img src={job.company_logo} alt={job.company_name} className="w-11 h-11 rounded-xl object-cover shrink-0 border border-gray-100" />
                        ) : (
                          <div className={`w-11 h-11 rounded-xl ${iconColors[colorIdx]} flex items-center justify-center text-lg font-bold shrink-0`}>
                            {job.title.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-gray-900 leading-snug group-hover:text-blue-900 transition-colors truncate">
                              {job.title}
                            </h3>
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${jobStatusColors[job.status] || 'bg-gray-100 text-gray-600'}`}>
                              {job.status.toUpperCase()}
                            </span>
                            {job.is_partner_listing && (
                              <span className="bg-yellow-400 text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5">
                                <Star className="w-3 h-3" /> PARTNER
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5 truncate">{job.company_name}</p>
                          {job.avg_rating != null && (
                            <div className="flex items-center gap-1 mt-0.5">
                              {[1,2,3,4,5].map(s => (
                                <Star key={s} className="w-3 h-3"
                                  fill={s <= Math.round(job.avg_rating!) ? '#FBBF24' : 'none'}
                                  stroke={s <= Math.round(job.avg_rating!) ? '#FBBF24' : '#D1D5DB'} />
                              ))}
                              <span className="text-xs text-gray-400 ml-0.5">{Number(job.avg_rating).toFixed(1)}</span>
                              {job.rating_count != null && (
                                <span className="text-xs text-gray-400">({job.rating_count})</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Location */}
                      {job.location && (
                        <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5 text-gray-400" /> {job.location}
                        </p>
                      )}

                      {/* Info row */}
                      <div className="flex items-center gap-2 mt-3 text-xs text-gray-600 flex-wrap">
                        {job.experience_level && (
                          <>
                            <span className="font-medium">{EXPERIENCE_LEVEL_LABELS[job.experience_level as keyof typeof EXPERIENCE_LEVEL_LABELS] || job.experience_level}</span>
                            <span className="text-gray-300">·</span>
                          </>
                        )}
                        <span>{EMPLOYMENT_TYPE_LABELS[job.employment_type as keyof typeof EMPLOYMENT_TYPE_LABELS] || job.employment_type.replace('_', ' ')}</span>
                        {job.salary_range && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span className="text-green-700 font-medium">{SALARY_LABELS[job.salary_range] || job.salary_range}</span>
                          </>
                        )}
                      </div>

                      {/* Description snippet */}
                      {job.description && (
                        <p className="text-sm text-gray-600 mt-3 line-clamp-2 leading-relaxed flex-1">
                          {job.description}
                        </p>
                      )}

                      {/* Skills tags */}
                      {(job.skills_required?.length || 0) > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {job.skills_required!.slice(0, 4).map(s => (
                            <span key={s} className="bg-gray-100 text-gray-700 text-[11px] font-medium px-2 py-0.5 rounded-md">{s}</span>
                          ))}
                          {job.skills_required!.length > 4 && (
                            <span className="bg-gray-100 text-gray-500 text-[11px] font-medium px-2 py-0.5 rounded-md">+{job.skills_required!.length - 4}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Card footer */}
                    <div className="px-5 py-3.5 border-t border-gray-100 space-y-2.5">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
                          <span><span className="font-medium text-gray-500">Posted:</span> {fmtJobDate(job.posted_at)}</span>
                          {job.deadline && (
                            <span className={`flex items-center gap-1 ${new Date(job.deadline) < new Date() ? 'text-red-500' : 'text-orange-500'}`}>
                              <Calendar className="w-3 h-3" />
                              <span className="font-medium">{new Date(job.deadline) < new Date() ? 'Expired:' : 'Deadline:'}</span>
                              {fmtJobDate(job.deadline)}
                            </span>
                          )}
                        </div>
                        {job.status === 'active' && (
                          <button
                            onClick={e => { e.stopPropagation(); setCloseTarget(job); setCloseReason('') }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs font-semibold hover:bg-red-100 transition shrink-0"
                          >
                            <XCircle className="w-3.5 h-3.5" />
                            Close
                          </button>
                        )}
                      </div>
                      {job.is_admin_closed ? (
                        <div className="flex items-center justify-center gap-2 py-1.5 bg-red-50 text-red-700 border border-red-200 text-xs font-semibold rounded-lg">
                          <ShieldAlert className="w-3.5 h-3.5" />
                          Closed by Admin — Applications Cancelled
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2 py-1.5 bg-blue-50 text-blue-900 text-xs font-semibold rounded-lg">
                          <Users className="w-3.5 h-3.5" />
                          {Number(job.application_count)} Application{Number(job.application_count) !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {filtered.length > 0 && jobTotalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2 pb-1">
              <button
                onClick={() => setJobPage(p => Math.max(1, p - 1))}
                disabled={jobPage === 1}
                className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <span className="text-sm text-gray-500">
                Page <span className="font-semibold text-gray-700">{jobPage}</span> of <span className="font-semibold text-gray-700">{jobTotalPages}</span>
              </span>
              <button
                onClick={() => setJobPage(p => Math.min(jobTotalPages, p + 1))}
                disabled={jobPage === jobTotalPages}
                className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed transition"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}

      {/* Job Detail Modal */}
      {viewingJob && createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setViewingJob(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[calc(100vh-4rem)] flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between px-6 py-4 border-b gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {viewingJob.company_logo ? (
                  <img src={viewingJob.company_logo} alt={viewingJob.company_name} className="w-11 h-11 rounded-xl object-cover shrink-0 border border-gray-100" />
                ) : (
                  <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center text-blue-700 text-lg font-bold shrink-0">
                    {viewingJob.title.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-bold text-gray-900 leading-snug">{viewingJob.title}</h2>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${jobStatusColors[viewingJob.status] || 'bg-gray-100 text-gray-600'}`}>
                      {viewingJob.status.toUpperCase()}
                    </span>
                    {viewingJob.is_partner_listing && (
                      <span className="bg-yellow-400 text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5">
                        <Star className="w-3 h-3" /> PARTNER
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">{viewingJob.company_name}</p>
                  {viewingJob.avg_rating != null && (
                    <div className="flex items-center gap-1 mt-0.5">
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} className="w-3.5 h-3.5"
                          fill={s <= Math.round(viewingJob.avg_rating!) ? '#FBBF24' : 'none'}
                          stroke={s <= Math.round(viewingJob.avg_rating!) ? '#FBBF24' : '#D1D5DB'} />
                      ))}
                      <span className="text-xs text-gray-500 ml-0.5">{Number(viewingJob.avg_rating).toFixed(1)}</span>
                      {viewingJob.rating_count != null && (
                        <span className="text-xs text-gray-400">({viewingJob.rating_count} {viewingJob.rating_count === 1 ? 'review' : 'reviews'})</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <button onClick={() => setViewingJob(null)} className="p-1 hover:bg-gray-100 rounded-lg shrink-0">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Key info pills */}
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">
                  <Briefcase className="w-3.5 h-3.5 text-gray-400" />
                  {EMPLOYMENT_TYPE_LABELS[viewingJob.employment_type as keyof typeof EMPLOYMENT_TYPE_LABELS] || viewingJob.employment_type.replace('_', ' ')}
                </span>
                {viewingJob.experience_level && (
                  <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">
                    {EXPERIENCE_LEVEL_LABELS[viewingJob.experience_level as keyof typeof EXPERIENCE_LEVEL_LABELS] || viewingJob.experience_level}
                  </span>
                )}
                {viewingJob.location && (
                  <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">
                    <MapPin className="w-3.5 h-3.5 text-gray-400" /> {viewingJob.location}
                  </span>
                )}
                {viewingJob.salary_range && (
                  <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-1 rounded-full font-medium">
                    {SALARY_LABELS[viewingJob.salary_range] || viewingJob.salary_range}
                  </span>
                )}
                {viewingJob.deadline && (
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-medium ${new Date(viewingJob.deadline) < new Date() ? 'bg-red-50 text-red-600' : 'bg-orange-50 text-orange-600'}`}>
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(viewingJob.deadline) < new Date() ? 'Expired' : 'Deadline'}: {fmtJobDate(viewingJob.deadline)}
                  </span>
                )}
              </div>

              {/* Close reason (if closed) */}
              {viewingJob.status === 'closed' && viewingJob.close_reason && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                  <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-700 mb-0.5">Closed by Admin</p>
                    <p className="text-sm text-red-600">{viewingJob.close_reason}</p>
                    {viewingJob.closed_by_name?.trim() && (
                      <p className="text-xs text-red-400 mt-1">Closed by {viewingJob.closed_by_name}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Description */}
              {viewingJob.description && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Description</h3>
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{viewingJob.description}</p>
                </div>
              )}

              {/* Requirements */}
              {(viewingJob.requirements?.length || 0) > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Requirements</h3>
                  <ul className="space-y-1">
                    {viewingJob.requirements!.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Skills */}
              {(viewingJob.skills_required?.length || 0) > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Skills Required</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {viewingJob.skills_required!.map(s => (
                      <span key={s} className="bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-md">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className={`grid ${viewingJob.is_admin_closed ? 'grid-cols-1' : 'grid-cols-2'} gap-3 pt-1`}>
                {!viewingJob.is_admin_closed && (
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500 mb-0.5">Applications</p>
                    <p className="text-2xl font-bold text-gray-900">{Number(viewingJob.application_count)}</p>
                  </div>
                )}
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Posted</p>
                  <p className="text-sm font-semibold text-gray-700 mt-1">{fmtJobDate(viewingJob.posted_at)}</p>
                </div>
              </div>

              {/* Posted by */}
              {viewingJob.posted_by_name?.trim() && (
                <p className="text-xs text-gray-400">Posted by <span className="font-medium text-gray-500">{viewingJob.posted_by_name}</span></p>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => setViewingJob(null)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition"
              >
                Close
              </button>
              {viewingJob.status === 'active' && (
                <button
                  onClick={() => { setViewingJob(null); setCloseTarget(viewingJob); setCloseReason('') }}
                  className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition"
                >
                  <XCircle className="w-4 h-4" />
                  Close Listing
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Close confirmation modal */}
      {closeTarget && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => { setCloseTarget(null); setCloseReason('') }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Close Job Listing</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    You are closing{' '}
                    <span className="font-semibold text-gray-700">"{closeTarget.title}"</span>{' '}
                    by <span className="font-semibold text-gray-700">{closeTarget.company_name}</span>.
                  </p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 mb-5 text-sm text-amber-700">
                The employer and all alumni with pending or active applications will be notified. This action cannot be undone.
              </div>
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reason for closing <span className="text-red-500">*</span>
                  <span className="ml-1 text-xs font-normal text-gray-400">(min. 10 characters)</span>
                </label>
                <textarea
                  rows={3}
                  value={closeReason}
                  onChange={e => setCloseReason(e.target.value)}
                  placeholder="e.g. This listing violates our content guidelines..."
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                />
                <p className={`text-xs mt-1 ${closeReason.trim().length < 10 ? 'text-gray-400' : 'text-green-600'}`}>
                  {closeReason.trim().length} / 10 minimum characters
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setCloseTarget(null); setCloseReason('') }}
                  className="px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-xl hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdminClose}
                  disabled={closing || closeReason.trim().length < 10}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <XCircle className="w-4 h-4" />
                  {closing ? 'Closing...' : 'Close Listing'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default AdminDashboard